import React from 'react';
import { useNavigate } from 'react-router-dom';
import { apiContext, clearAuthSession, setAuthSession } from '../Api';
import { keycloakConfig } from '../keycloak';

const icons = {
  shield: <path d="M12 3 5 6v5c0 4.2 2.9 8.1 7 10 4.1-1.9 7-5.8 7-10V6l-7-3Z" />,
  warning: <path d="M12 9v4m0 4h.01M10.3 4.6 2.7 18a2 2 0 0 0 1.7 3h15.2a2 2 0 0 0 1.7-3L13.7 4.6a2 2 0 0 0-3.4 0Z" />,
  arrowLeft: <path d="M19 12H5m6 7-7-7 7-7" />,
};

function Icon({ name, className = 'h-4 w-4' }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {icons[name]}
    </svg>
  );
}

function LoginCallback() {
  const navigate = useNavigate();
  const { axios } = React.useContext(apiContext);
  const [status, setStatus] = React.useState('Memproses login SSO...');
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const handleCallback = async () => {
      if (!keycloakConfig.hasKeycloakConfig) {
        setStatus('Konfigurasi SSO belum lengkap.');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const error = params.get('error_description') || params.get('error');

      if (error) {
        setStatus(error);
        setLoading(false);
        return;
      }

      const savedState = sessionStorage.getItem('kc_state');
      const codeVerifier = sessionStorage.getItem('kc_code_verifier');

      if (!code || !state || !savedState || !codeVerifier || state !== savedState) {
        setStatus('Callback SSO tidak valid. Silakan ulangi login.');
        setLoading(false);
        return;
      }

      try {
        const appLoginRes = await axios.post('/login/keycloak/callback', {
          code,
          code_verifier: codeVerifier,
          redirect_uri: keycloakConfig.redirectUri,
        });

        if (!appLoginRes.data.access_token || !appLoginRes.data.id) {
          throw new Error('Response login SSO tidak valid.');
        }

        clearAuthSession();
        setAuthSession(appLoginRes.data.access_token, appLoginRes.data.id);

        sessionStorage.removeItem('kc_state');
        sessionStorage.removeItem('kc_code_verifier');

        window.location.replace('/dashboard');
      } catch (err) {
        setStatus(err?.response?.data?.message || err?.message || 'Login SSO gagal.');
        setLoading(false);
      }
    };

    handleCallback();
  }, [axios, navigate]);

  return (
    <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-brand-soft px-4 text-center text-brand-navy">
      <div className="absolute inset-x-0 top-0 h-64 bg-brand-navy" />
      <div className="absolute -right-24 top-12 h-72 w-72 rounded-full bg-brand-blue/25 blur-3xl" />
      <div className="absolute -left-24 bottom-12 h-72 w-72 rounded-full bg-brand-green/20 blur-3xl" />

      <section className="relative w-full max-w-md rounded-lg border border-white/70 bg-white p-7 shadow-2xl shadow-slate-900/15 sm:p-9">
        <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-lg bg-brand-soft text-brand-blue">
          {loading ? (
            <span className="h-8 w-8 animate-spin rounded-full border-4 border-brand-blue/20 border-t-brand-blue" />
          ) : (
            <Icon name="warning" className="h-8 w-8 text-red-600" />
          )}
        </div>

        <h1 className="mt-6 font-display text-2xl font-extrabold">
          {loading ? 'Memvalidasi SSO' : 'Login belum berhasil'}
        </h1>
        <p className="mx-auto mt-3 max-w-sm text-sm font-semibold leading-6 text-slate-600 sm:text-base">
          {status}
        </p>

        <div className="mt-6 rounded-lg border border-slate-200 bg-brand-soft p-4 text-left text-xs leading-6 text-slate-600">
          <div className="mb-2 inline-flex items-center gap-2 font-extrabold text-brand-navy">
            <Icon name="shield" className="h-4 w-4 text-brand-green" />
            Alur autentikasi
          </div>
          <p>Callback Keycloak sedang ditukar menjadi token aplikasi dan sesi pengguna.</p>
        </div>

        {!loading ? (
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-brand-blue px-5 text-sm font-bold text-white shadow-sm transition hover:bg-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2"
          >
            <Icon name="arrowLeft" />
            Kembali ke Login
          </button>
        ) : null}
      </section>
    </main>
  );
}

export default LoginCallback;
